// Эндпоинты и типы для управления выводами (админ)

export type WithdrawalStatus = "pending" | "processing" | "completed" | "cancelled";

export interface WithdrawalRequest {
    id: number;
    user_id: number;
    surname: string;
    name: string;
    photo?: string;
    telegram_id?: string | null;
    currency: string;
    amount: number;
    status: WithdrawalStatus;
    method_flags?: string | null;
    bep20_address?: string | null;
    trc20_address?: string | null;
    ton_address?: string | null;
    card_number?: string | null;
    altyn_crypto_wallet?: string | null;
    altyn_sbp_bank_id?: string | null;
    altyn_sbp_bank_name?: string | null;
    altyn_payout_id?: number | null;
    description?: string | null;
    created_at: string;
    // Дополнительная агрегированная информация по пользователю
    bep20?: string | null;
    trc20?: string | null;
    ton?: string | null;
    bank_card?: string | null;
    bank_account?: string | null;
    clients_total: number;
    total_referrals: number;
    managers_total: number;
    main_wallet: number;
    usdt_wallet: number;
    total_deposited: number;
    usdt_total_deposited: number;
    total_earned: number;
    usdt_total_earned: number;
    main_monthly_profit: number;
    usdt_monthly_profit: number;
    source?: string | null;
    is_frozen?: boolean;
}

export interface WithdrawalHistoryItem extends WithdrawalRequest {
    user_name?: string | null;
    user_surname?: string | null;
    processed_at?: string | null;
    cancelled_at?: string | null;
    admin_id?: number | null;
    admin_name?: string | null;
    admin_surname?: string | null;
}

export interface WithdrawalHistoryResponse {
    success: boolean;
    data: {
        items: WithdrawalHistoryItem[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
}

export interface ConfirmWithdrawalRequest {
    request_id: number; // ID заявки из wallet_withdrawal_requests
    currency: number; // 0 USD (счет), 1 USDT (CryptoBot), -1 USDT (card/chain), -2 USD (card), -3 USD (chain), 100 ALNY
    account?: string; // bank account or telegram_id or chain address, depending on currency
    card?: string; // bank card
}

export interface CancelWithdrawalRequest {
    user_id: number;
    currency: number; // 0 USD, 1 USDT
    comment: string;
}

export interface ConfirmAlnyWithdrawalRequest {
    user_id: number;
    sum?: number;
}

export interface CancelAlnyWithdrawalRequest {
    user_id: number;
    comment?: string;
}

export const withdrawalsEndpoints = {
    getPendingWithdrawals: {
        query: () => "/admin/withdrawals/pending",
    },

    getWithdrawalHistory: {
        query: (params?: {
            page?: number;
            per_page?: number;
            status?: string;
            currency?: string;
            source?: string;
            user_id?: number;
        }) => ({
            url: "/admin/withdrawals/history",
            params,
        }),
    },

    confirmWithdrawal: {
        // Кастомный queryFn, чтобы парсить ответы вида "path/to/file.jpg{...json...}"
        queryFn: async (
            body: ConfirmWithdrawalRequest,
            _api: any,
            _extraOptions: any,
            baseQuery: any
        ) => {
            const result = await baseQuery({
                url: "/admin/withdrawals/confirm",
                method: "POST",
                body,
                responseHandler: "text",
            });

            if (result.error) {
                // Если ошибка содержит строковый data, пытаемся распарсить JSON
                if (result.error.data && typeof result.error.data === "string") {
                    try {
                        const parsedError = JSON.parse(result.error.data);
                        return {
                            error: {
                                ...result.error,
                                data: parsedError,
                            },
                        };
                    } catch {
                        // Если не удалось распарсить, оставляем как есть
                    }
                }
                return { error: result.error };
            }

            const data = result.data as unknown as string;
            if (typeof data === "string") {
                const trimmed = data.trim();
                const braceIndex = trimmed.indexOf("{");
                if (braceIndex >= 0) {
                    const jsonPart = trimmed.slice(braceIndex);
                    try {
                        return { data: JSON.parse(jsonPart) };
                    } catch {
                        return { data: { raw: trimmed } };
                    }
                }
                return { data: { raw: trimmed } };
            }

            return { data };
        },
    },

    cancelWithdrawal: {
        query: (body: CancelWithdrawalRequest) => ({
            url: "/admin/withdrawals/cancel",
            method: "POST",
            body,
        }),
    },

    getPendingAlnyWithdrawals: {
        query: () => "/admin/balance/alny-withdrawals/pending",
    },

    confirmAlnyWithdrawal: {
        query: (body: ConfirmAlnyWithdrawalRequest) => ({
            url: "/admin/balance/alny-withdrawals/confirm",
            method: "POST",
            body,
        }),
    },

    cancelAlnyWithdrawal: {
        query: (body: CancelAlnyWithdrawalRequest) => ({
            url: "/admin/balance/alny-withdrawals/cancel",
            method: "POST",
            body,
        }),
    },
    freezeWithdrawal: {
        query: (body: { id: number }) => ({
            url: "/admin/withdrawals/freeze",
            method: "POST",
            body,
        }),
    },
};

export const withdrawalsTags = [
    "PendingWithdrawals",
    "PendingAlnyWithdrawals",
] as const;
